import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Zap, Wallet, RefreshCw, CheckCircle2, AlertTriangle,
  Info, Plus, ArrowRight, ExternalLink, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GLABS_MINT = 'GHu22mtb6bu1GzzirCR54eZYYDxUxyN5r7K5Ly89pump';

const CURRENCY_META = {
  GLABS: { label: 'GLABS', chain: 'solana', color: '#E8520F', desc: 'Native platform token' },
  USDT:  { label: 'USDT',  chain: 'solana', color: '#26A17B', desc: 'Tether USD (Solana)'   },
  SOL:   { label: 'SOL',   chain: 'solana', color: '#9945FF', desc: 'Solana native token'   },
  USDC:  { label: 'USDC',  chain: 'solana', color: '#2775CA', desc: 'USD Coin (Solana)'     },
};

const CRYPTO_RATES = { SOL: 150, ETH: 3000, USDC: 1, USDT: 1, GLABS: 0 };

export default function BuyCredits() {
  const { user } = useAuth();
  const [pricePerCredit, setPricePerCredit] = useState(0.25);
  const [glabsPrice, setGlabsPrice]         = useState(0);
  const [wallets, setWallets]               = useState([]);
  const [currency, setCurrency]             = useState('GLABS');
  const [amount, setAmount]                 = useState('');
  const [loading, setLoading]               = useState(false);
  const [connecting, setConnecting]         = useState(false);
  const [credits, setCredits]               = useState(user?.credits || 0);
  const [lastTx, setLastTx]                 = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [priceRes, walletRes, balRes, glabsRes] = await Promise.all([
        axios.get(`${API_URL}/api/glabs/balance`,      { withCredentials: true }),
        axios.get(`${API_URL}/api/wallet/connected`,   { withCredentials: true }),
        axios.get(`${API_URL}/api/credits/balance`,    { withCredentials: true }),
        axios.get(`${API_URL}/api/glabs/token-price`,  { withCredentials: true }).catch(() => ({ data: { price_usd: 0 } })),
      ]);
      setPricePerCredit(priceRes.data.glabs_rate_usdt || 0.25);
      setWallets(walletRes.data.wallets || []);
      setCredits(balRes.data.credits || 0);
      setGlabsPrice(glabsRes.data.price_usd || 0);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const effectiveRate = currency === 'GLABS'
    ? (glabsPrice > 0 ? glabsPrice : 0)
    : (CRYPTO_RATES[currency] || 1);

  const usdtValue = (() => {
    const n = parseFloat(amount);
    if (!n || n <= 0) return 0;
    if (currency === 'GLABS') return glabsPrice > 0 ? n * glabsPrice : 0;
    return n * (CRYPTO_RATES[currency] || 1);
  })();

  const creditsPreview = pricePerCredit > 0 ? Math.floor(usdtValue / pricePerCredit) : 0;

  const handleConnectPhantom = async () => {
    setConnecting(true);
    try {
      if (window.solana?.isPhantom) {
        const resp = await window.solana.connect({ onlyIfTrusted: false });
        const address = resp.publicKey.toString();
        await axios.post(`${API_URL}/api/wallet/connect`,
          { chain: 'solana', address, wallet_type: 'phantom' },
          { withCredentials: true });
        toast.success(`Phantom connected: ${address.slice(0,6)}...${address.slice(-4)}`);
        fetchData();
      } else {
        toast.error('Phantom not installed. Visit phantom.app');
      }
    } catch { toast.error('Failed to connect Phantom'); }
    setConnecting(false);
  };

  // Load Solana web3 libraries as UMD scripts (no dynamic import — CRA compatible)
  const loadSolanaLibs = () => new Promise((resolve, reject) => {
    if (window.solanaWeb3 && window.splToken) { resolve(); return; }
    const loadScript = (src, id) => new Promise((res, rej) => {
      if (document.getElementById(id)) { res(); return; }
      const s = document.createElement('script');
      s.id = id; s.src = src; s.async = false;
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
    // Load UMD builds — expose window.solanaWeb3 and window.splToken globals
    loadScript('https://unpkg.com/@solana/web3.js@1.95.4/lib/index.iife.min.js', 'solana-web3-umd')
      .then(() => loadScript('https://unpkg.com/@solana/spl-token@0.4.9/lib/index.iife.min.js', 'spl-token-umd'))
      .then(resolve)
      .catch(reject);
  });

  // Automatic Phantom GLabs payment using UMD globals
  const handleBuyWithGlabs = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0 || creditsPreview < 1) {
      toast.error('Enter a valid amount');
      return;
    }
    const phantomWallet = wallets.find(w => w.chain === 'solana');
    if (!phantomWallet) {
      toast.error('Connect Phantom first');
      return;
    }
    if (!window.solana?.isPhantom) {
      toast.error('Phantom extension not found');
      return;
    }
    // GLabs requires liquidity to be useful — show info if price is $0
    if (glabsPrice === 0) {
      toast.error('GLabs has no active market price. Add liquidity first or use USDT/SOL to buy credits.');
      return;
    }
    setLoading(true);
    try {
      // 1. Get payment details from backend
      const { data: payInfo } = await axios.post(`${API_URL}/api/glabs/prepare-payment`, {
        user_wallet_address: phantomWallet.address,
        credits_amount: creditsPreview,
      }, { withCredentials: true });

      toast.info('Loading Solana libraries…');

      // 2. Load UMD scripts at runtime (no webpack dynamic import needed)
      await loadSolanaLibs();
      const { Connection, PublicKey, Transaction } = window.solanaWeb3;
      const { getAssociatedTokenAddress, createTransferInstruction, TOKEN_PROGRAM_ID } = window.splToken;

      if (!Connection || !PublicKey) {
        throw new Error('Solana web3 library failed to load. Try again.');
      }

      toast.info('Opening Phantom to approve GLabs transfer…');

      const connection  = new Connection('https://api.mainnet-beta.solana.com', 'confirmed');
      const userPubkey  = new PublicKey(phantomWallet.address);
      const adminPubkey = new PublicKey(payInfo.admin_wallet);
      const mintPubkey  = new PublicKey(GLABS_MINT);

      // 3. Derive ATAs
      const userATA  = await getAssociatedTokenAddress(mintPubkey, userPubkey);
      const adminATA = await getAssociatedTokenAddress(mintPubkey, adminPubkey);

      // 4. Build transfer instruction
      const transferIx = createTransferInstruction(
        userATA, adminATA, userPubkey,
        BigInt(payInfo.glabs_raw_amount), [], TOKEN_PROGRAM_ID
      );

      const tx = new Transaction().add(transferIx);
      const { blockhash } = await connection.getLatestBlockhash();
      tx.recentBlockhash = blockhash;
      tx.feePayer = userPubkey;

      // 5. Sign + send via Phantom
      const { signature } = await window.solana.signAndSendTransaction(tx);
      toast.success('Transaction sent! Verifying…');

      // 6. Verify on backend
      await new Promise(r => setTimeout(r, 2000));
      const { data: result } = await axios.post(`${API_URL}/api/glabs/verify-payment`, {
        tx_signature: signature,
        expected_credits: creditsPreview,
      }, { withCredentials: true });

      setCredits(result.credits);
      setLastTx({ sig: signature, credits: result.credits_added });
      setAmount('');
      toast.success(`✅ ${result.credits_added} credits added!`);
      fetchData();
    } catch (err) {
      if (err?.message?.includes('rejected')) {
        toast.error('Transaction rejected in Phantom');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Payment failed');
      }
    }
    setLoading(false);
  };

  // USDT/SOL/USDC purchase (existing flow)
  const handleBuyWithFiat = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0 || creditsPreview < 1) {
      toast.error('Enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/credits/buy`,
        { currency, amount: n }, { withCredentials: true });
      toast.success(`✅ ${data.credits_added} credits added!`);
      setCredits(data.credits);
      setAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    }
    setLoading(false);
  };

  const handleBuy = () => currency === 'GLABS' ? handleBuyWithGlabs() : handleBuyWithFiat();

  const hasWallet = wallets.length > 0;
  const PRESETS_USD = [5, 10, 25, 50, 100];

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
          <Zap className="w-7 h-7 text-[#D4A017]" /> Buy Credits
        </h1>
        <p className="text-[hsl(215,16%,56%)]">
          Credits power your bots. 1 credit per bot start · 5 credits per withdrawal.
        </p>
      </div>

      {/* Balance */}
      <Card className="border-[hsl(220,16%,18%)] bg-gradient-to-br from-[#D4A017]/5 to-transparent shadow-none">
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <p className="text-xs text-[hsl(215,16%,56%)] mb-1">Current Balance</p>
            <p className="text-4xl font-bold text-[#D4A017] font-mono">{credits}</p>
            <p className="text-xs text-[hsl(215,16%,45%)]">credits</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[hsl(215,16%,56%)]">Price per credit</p>
            <p className="text-2xl font-bold text-white font-mono">${pricePerCredit.toFixed(2)}</p>
            {glabsPrice > 0 && (
              <p className="text-xs text-[#E8520F] mt-1">
                GLABS: ${glabsPrice.toFixed(8)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Last tx confirmation */}
      {lastTx && (
        <Card className="border-[#10B981]/30 bg-[#10B981]/5 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#10B981] shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#10B981]">+{lastTx.credits} credits added</p>
              <a href={`https://solscan.io/tx/${lastTx.sig}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-[#10B981] hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> View on Solscan
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wallet */}
      {!hasWallet ? (
        <Card className="border-amber-500/30 bg-amber-500/5 shadow-none">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-400">No wallet connected</p>
              <Button onClick={handleConnectPhantom} disabled={connecting}
                className="mt-3 bg-gradient-to-r from-[#AB9FF2] to-[#6366F1] text-white text-xs h-8">
                {connecting ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Connect Phantom
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardContent className="p-3 flex items-center gap-3">
            <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
            <span className="text-xs font-mono text-white truncate">{wallets[0]?.address}</span>
            <Button onClick={handleConnectPhantom} disabled={connecting} variant="outline" size="sm"
              className="ml-auto border-[hsl(220,16%,18%)] text-white bg-transparent text-xs h-7 shrink-0">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Buy form */}
      <Card className="border-[hsl(220,16%,18%)] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#D4A017]" /> Purchase Credits
          </CardTitle>
          <CardDescription>GLABS uses Phantom automatic flow — no manual steps.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Currency selector */}
          <div className="space-y-2">
            <Label className="text-white text-xs">Pay With</Label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(CURRENCY_META).map(([cur, meta]) => (
                <button key={cur} type="button" onClick={() => setCurrency(cur)}
                  className={`p-2 rounded-lg border text-center transition-all ${
                    currency === cur
                      ? 'border-[#D4A017] bg-[#D4A017]/10'
                      : 'border-[hsl(220,16%,18%)] hover:border-[hsl(220,16%,28%)]'
                  }`}>
                  <p className="text-sm font-bold text-white">{meta.label}</p>
                  <p className="text-[10px] text-[hsl(215,16%,45%)]">{meta.chain}</p>
                  {cur === 'GLABS' && <p className="text-[9px] text-[#E8520F] mt-0.5">Pump.fun</p>}
                </button>
              ))}
            </div>
          </div>

          {/* GLABS note */}
          {currency === 'GLABS' && (
            <div className="p-3 bg-[#E8520F]/5 border border-[#E8520F]/20 rounded-lg flex items-start gap-2">
              <Shield className="w-4 h-4 text-[#E8520F] shrink-0 mt-0.5" />
              <div className="text-[10px] text-[hsl(215,16%,56%)] space-y-0.5">
                <p className="font-medium text-[#E8520F]">Automatic Phantom flow</p>
                <p>Click Buy → Phantom popup → Approve → Credits added instantly.</p>
                <p>Mint: <span className="font-mono">{GLABS_MINT.slice(0,12)}…</span></p>
                {glabsPrice > 0
                  ? <p>Live price: <span className="text-white">${glabsPrice.toFixed(8)}</span></p>
                  : <p className="text-amber-400">Price loading from Jupiter…</p>}
              </div>
            </div>
          )}

          {/* Amount input */}
          <div className="space-y-2">
            <Label className="text-white text-xs">Amount ({currency})</Label>
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder={`e.g. ${currency === 'GLABS' ? '1000' : '10'} ${currency}`}
              className="border-[hsl(220,16%,18%)] text-white font-mono" step="0.01" />
            {/* USD presets */}
            <div className="flex gap-2 flex-wrap">
              {PRESETS_USD.map(p => {
                const tokAmt = currency === 'GLABS'
                  ? (glabsPrice > 0 ? (p / glabsPrice).toFixed(0) : '—')
                  : (p / (CRYPTO_RATES[currency] || 1)).toFixed(currency === 'SOL' ? 4 : 0);
                return (
                  <button key={p} type="button"
                    onClick={() => tokAmt !== '—' && setAmount(String(tokAmt))}
                    disabled={tokAmt === '—'}
                    className="px-2 py-1 rounded text-[10px] font-mono bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)] text-[hsl(215,16%,56%)] hover:text-white hover:border-[#D4A017] transition-colors disabled:opacity-40">
                    ${p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          {amount && parseFloat(amount) > 0 && (
            <div className="p-4 bg-[#D4A017]/5 border border-[#D4A017]/30 rounded-xl space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[hsl(215,16%,56%)]">You spend</span>
                <span className="font-mono text-white font-bold">{amount} {currency}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[hsl(215,16%,56%)]">USD value</span>
                <span className="font-mono text-white">{usdtValue > 0 ? `$${usdtValue.toFixed(4)}` : '—'}</span>
              </div>
              <div className="border-t border-[#D4A017]/20 pt-2 flex items-center justify-between">
                <span className="text-white font-medium">Credits you receive</span>
                <span className="text-2xl font-bold text-[#D4A017] font-mono">
                  {creditsPreview > 0 ? creditsPreview : '—'}
                </span>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="p-3 bg-[hsl(220,16%,14%)] rounded-lg border border-[hsl(220,16%,18%)] flex items-start gap-2">
            <Info className="w-4 h-4 text-[hsl(215,16%,56%)] shrink-0 mt-0.5" />
            <div className="text-[10px] text-[hsl(215,16%,56%)] space-y-0.5">
              <p>• 1 credit = start 1 bot</p>
              <p>• 5 credits = 1 withdrawal</p>
              <p>• GLABS is the native token — best rate</p>
            </div>
          </div>

          <Button onClick={handleBuy}
            disabled={loading || !hasWallet || creditsPreview < 1 || (currency === 'GLABS' && glabsPrice === 0 && !window?.solana)}
            className="w-full bg-[#D4A017] hover:bg-[#B8860B] text-white font-bold h-12 text-base">
            {loading
              ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              : <><Zap className="w-5 h-5 mr-2" />
                {currency === 'GLABS' ? 'Pay with GLABS via Phantom' : `Buy ${creditsPreview > 0 ? creditsPreview : ''} Credits`}
                <ArrowRight className="w-4 h-4 ml-2" /></>
            }
          </Button>
        </CardContent>
      </Card>

      {/* Usage table */}
      <Card className="border-[hsl(220,16%,18%)] shadow-none">
        <CardContent className="p-4">
          <p className="text-xs text-[hsl(215,16%,45%)] uppercase tracking-wider mb-3">Credit usage</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { action: 'Start Volume Bot', cost: 1 },
              { action: 'Start Lure Bot',   cost: 1 },
              { action: 'Withdraw Earnings',cost: 5 },
              { action: '$10 PnL milestone',cost: 1 },
            ].map(({ action, cost }) => (
              <div key={action} className="flex items-center justify-between p-2 rounded-lg bg-[hsl(220,16%,14%)]">
                <span className="text-xs text-[hsl(215,16%,56%)]">{action}</span>
                <span className="text-xs font-mono font-bold text-[#D4A017]">{cost}c</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
